import { useState, useRef, useEffect } from 'react';
import { Bell } from 'lucide-react';
import { apiPost } from '../lib/api';
import { useToast } from '../contexts/ToastContext';

interface Props {
  entityType: string;
  entityId: string;
  entityTitle: string;
}

const PRESETS = [
  { label: 'In 15 min', minutes: 15 },
  { label: 'In 1 hour', minutes: 60 },
  { label: 'In 4 hours', minutes: 240 },
  { label: 'Tomorrow 9 AM', minutes: -1 },
  { label: 'Next Monday 9 AM', minutes: -2 },
];

function getRemindAt(minutes: number): string {
  if (minutes === -1) {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(9, 0, 0, 0);
    return d.toISOString();
  }
  if (minutes === -2) {
    const d = new Date();
    const daysUntilMonday = (8 - d.getDay()) % 7 || 7;
    d.setDate(d.getDate() + daysUntilMonday);
    d.setHours(9, 0, 0, 0);
    return d.toISOString();
  }
  return new Date(Date.now() + minutes * 60 * 1000).toISOString();
}

export function RemindMeButton({ entityType, entityId, entityTitle }: Props) {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  async function handlePreset(minutes: number) {
    try {
      await apiPost('/reminders', {
        title: entityTitle,
        remind_at: getRemindAt(minutes),
        entity_type: entityType,
        entity_id: entityId,
      });
      toast.success('Reminder set');
      setOpen(false);
    } catch {
      toast.error('Failed to create reminder');
    }
  }

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1 px-2 py-1 text-xs text-text-muted hover:text-accent hover:bg-bg-hover rounded transition-colors"
        title="Set a reminder"
      >
        <Bell size={13} />
        <span>Remind me</span>
      </button>
      {open && (
        <div className="absolute right-0 top-7 z-20 bg-bg-surface border border-border rounded-lg shadow-xl py-1 w-44">
          {PRESETS.map(p => (
            <button
              key={p.label}
              onClick={() => handlePreset(p.minutes)}
              className="w-full px-3 py-1.5 text-xs text-text-secondary hover:bg-bg-hover hover:text-text-primary text-left transition-colors"
            >
              {p.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
